<template>
    <div v-if="reviews.length > 0" class="reviews-slider">
        <h2 class="text--28px text-primary mb-4 ms-3">{{ reviewsTitle }}</h2>
        <div class="reviews-slider__container">
            <div
                class="slider-track"
                ref="track"
                @scroll="onScroll"
                @mousedown="startDrag"
            >
                <div
                    v-for="review in reviews"
                    :key="review.id"
                    class="slide"
                >
                    <div class="review-card">
                        <div class="review-card__header">
                            <div class="review-card__stars">
                                <svg
                                    v-for="i in Math.floor(review.rating)"
                                    :key="`star-${i}`"
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="16"
                                    height="16"
                                    viewBox="0 0 576 512"
                                >
                                    <path
                                        fill="#ffc107"
                                        d="M259.3 17.8L194 150.2 47.9 171.5c-26.2 3.8-36.7 36-17.7 54.6l105.7 103-25 145.5c-4.5 26.1 23 46 46.4 33.7L288 439.6l130.7 68.7c23.4 12.3 50.9-7.6 46.4-33.7l-25-145.5 105.7-103c19-18.6 8.5-50.8-17.7-54.6L382 150.2 316.7 17.8c-11.7-23.6-45.6-23.9-57.4 0z"
                                    />
                                </svg>
                            </div>
                            <img
                                src="/images/google-my-business-reviews-logo.png"
                                alt="Google My Business Reviews"
                                class="review-card__logo"
                            />
                        </div>
                        <template v-if="review.link">
                            <a
                                :href="review.link"
                                class="review-card__name review-card__name--link"
                                target="_blank"
                                rel="noopener"
                            >{{ review.name }}</a>
                        </template>
                        <template v-else>
                            <p class="review-card__name">{{ review.name }}</p>
                        </template>
                        <p class="review-card__content">{{ truncate(review.content, 150) }}</p>
                        <p class="review-card__showroom">{{ review.showroom?.name }}</p>
                    </div>
                </div>
            </div>

            <div class="slider-dots">
                <button
                    v-for="n in totalSlides"
                    :key="n"
                    :class="['dot', { 'dot--active': currentSlide === (n - 1) }]"
                    @click="scrollToSlide(n - 1)"
                    aria-label="Go to slide"
                />
            </div>
        </div>
    </div>
</template>

<script setup>
import { useReviewSlider } from '@/composables/useReviewSlider'

/** Props */
const props = defineProps({
    siteId:       { type: Number, required: true },
    lang:         { type: String, required: true },
    reviewsTitle: { type: String, required: true },
})

// Use the composable for shared logic
const {
    // State
    reviews,
    track,
    currentSlide,
    totalSlides,

    // Methods
    onScroll,
    scrollToSlide,
    startDrag,
    truncate
} = useReviewSlider(props)
</script>

<style scoped>
.reviews-slider {
    background: #fff;
    padding: 2rem 1rem;
    margin-top: 2rem;
}
.reviews-slider__title {
    text-align: left;
    font-size: 1.75rem;
    color: #2950ad;
    margin-bottom: 1.5rem;
}
.reviews-slider__container {
    position: relative;
    max-width: 1200px;
    margin: 0 auto;
}
/* Disable text selection and enable grab cursor */
.slider-track,
.slider-track * {
    user-select: none;
    -webkit-user-select: none;
    -ms-user-select: none;
}
.slider-track {
    display: flex;
    gap: 16px;
    overflow-x: auto;
    scroll-snap-type: x mandatory;
    scroll-behavior: smooth;
    -webkit-overflow-scrolling: touch;
    padding-bottom: 1rem;
    scrollbar-width: none;
    -ms-overflow-style: none;
    cursor: grab;
}
.slider-track.dragging {
    cursor: grabbing;
}
.slider-track::-webkit-scrollbar {
    display: none;
}
.slide {
    flex: 0 0 100%;
    scroll-snap-align: start;
}
@media (min-width: 768px) {
    .slide {
        flex: 0 0 calc((100% - 32px) / 3);
    }
}
.review-card {
    background: #f8f9fa;
    border-radius: 8px;
    padding: 1.25rem;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}
.review-card__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 0.75rem;
}
.review-card__stars {
    display: inline-flex;
}
.review-card__name {
    font-weight: 600;
    margin-bottom: 0.5rem;
    color: #333;
}
.review-card__name--link {
    color: #2950ad;
    text-decoration: none;
}
.review-card__name--link:hover {
    text-decoration: underline;
}
.review-card__content {
    font-size: 0.9rem;
    color: #555;
    margin-bottom: 0.5rem;
    line-height: 1.4;
}
.review-card__showroom {
    font-size: 0.8rem;
    color: #777;
}

/* Pagination dots */
.slider-dots {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 8px;
}
.dot {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: #dee2e6;
    border: none;
    cursor: pointer;
    padding: 0;
}
.dot--active {
    background: #2950ad;
    width: 15px;
    height: 15px;
}
</style>
